import React, { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { MenuIcon, PhoneIcon, XIcon } from 'lucide-react';
import { Logo, LogoColor } from './Logo';
import { useScrolled } from '../hooks/useScrolled';
import { navLinks } from '../data/navigation';
import { shop } from '../data/shop';
type SiteHeaderProps = {
  logoColor?: LogoColor;
};
export function SiteHeader({
  logoColor = 'purple'
}: SiteHeaderProps) {
  const [menuOpen, setMenuOpen] = useState(false);
  const scrolled = useScrolled();
  return <header className={`sticky top-0 z-40 border-b bg-surface transition-[border-color,box-shadow] duration-200 ${scrolled ? 'border-line shadow-sm' : 'border-transparent'}`} data-id="element-284" data-mp="qy89jy8sza0z">
      <div className={`mx-auto flex max-w-7xl items-center justify-between gap-6 px-6 transition-[height] duration-200 ease-out lg:px-8 ${scrolled ? 'h-[76px]' : 'h-28 lg:h-36'}`} data-id="element-285" data-mp="zvr1znos0qmw">
        <a href="#top" aria-label={`${shop.name} home`} className="shrink-0 rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent" data-id="element-286" data-mp="2belbbmj9ydfq">
          <Logo color={logoColor} className={`w-auto transition-[height] duration-200 ease-out ${scrolled ? 'h-14' : 'h-24 lg:h-[124px]'}`} data-id="element-287" data-mp="2ekq1x3l6g6pq" />
        </a>

        <nav aria-label="Primary" className="hidden xl:block" data-id="element-288" data-mp="1qtcbx4ai0n7x">
          <ul className="flex items-center gap-7" data-id="element-289" data-mp="1qv5ikjl437fx">
            {navLinks.map(link => <li key={link.href} data-id="element-290" data-mp="1vsx6l2imy9eu">
                <a href={link.href} className="whitespace-nowrap text-sm font-medium text-muted underline decoration-transparent decoration-2 underline-offset-[6px] transition-[color,text-decoration-color] duration-150 hover:text-accent hover:decoration-accent focus-visible:text-accent focus-visible:decoration-accent focus-visible:outline-none" data-id="element-291" data-mp="20ri958ld156g">
                  {link.label}
                </a>
              </li>)}
          </ul>
        </nav>

        <div className="flex items-center gap-3" data-id="element-292" data-mp="102eq6h24pcxy">
          <a href={shop.phoneHref} className="hidden items-center gap-2 whitespace-nowrap text-sm font-semibold text-ink md:flex" data-id="element-293" data-mp="101ls04h8jvrf">
            <PhoneIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-294" data-mp="19role5s9rbqa" />
            {shop.phone}
          </a>
          <a href="#estimate" className="hidden whitespace-nowrap rounded-md bg-accent px-4 py-2.5 text-sm font-semibold text-white transition-colors duration-150 hover:bg-accent-strong focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 sm:inline-flex" data-id="element-295" data-mp="m35bxmxhgswm">
            Free estimate
          </a>
          <button type="button" onClick={() => setMenuOpen(open => !open)} aria-expanded={menuOpen} aria-controls="mobile-menu" aria-label={menuOpen ? 'Close menu' : 'Open menu'} className="flex h-10 w-10 items-center justify-center rounded-md text-ink hover:bg-paper focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent xl:hidden" data-id="element-296" data-mp="27excqheybf07">
            {menuOpen ? <XIcon className="h-5 w-5" data-id="element-297" data-mp="211je9ctyv34k" /> : <MenuIcon className="h-5 w-5" data-id="element-298" data-mp="250s3c4e2acts" />}
          </button>
        </div>
      </div>

      <AnimatePresence data-id="element-299" data-mp="2sqmg6lyds1e1">
        {menuOpen && <motion.nav id="mobile-menu" aria-label="Mobile" initial={{
        opacity: 0,
        y: -8
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: -8
      }} transition={{
        duration: 0.2,
        ease: [0.23, 1, 0.32, 1]
      }} className="border-t border-line bg-surface xl:hidden" data-id="element-300" data-mp="2srxjylrtt955">
            <ul className="mx-auto max-w-7xl px-6 py-4" data-id="element-301" data-mp="3bpqnnqa9xn3l">
              {navLinks.map(link => <li key={link.href} data-id="element-302" data-mp="3gfyqjp5g2fhx">
                  <a href={link.href} onClick={() => setMenuOpen(false)} className="block py-3 text-base font-medium text-ink underline decoration-transparent decoration-2 underline-offset-[6px] transition-[color,text-decoration-color] duration-150 hover:text-accent hover:decoration-accent" data-id="element-303" data-mp="3gh0bclc9igcu">
                    {link.label}
                  </a>
                </li>)}
              <li className="mt-3 flex flex-col gap-3 border-t border-line pt-5 sm:hidden" data-id="element-304" data-mp="2biwnbra9kzrz">
                <a href="#estimate" onClick={() => setMenuOpen(false)} className="rounded-md bg-accent px-4 py-3 text-center text-sm font-semibold text-white" data-id="element-305" data-mp="26jk37eas26t3">
                  Get a free estimate
                </a>
                <a href={shop.phoneHref} className="flex items-center justify-center gap-2 py-2 text-sm font-semibold text-ink" data-id="element-306" data-mp="2u8uvs8hz4uw5">
                  <PhoneIcon className="h-4 w-4 text-accent" aria-hidden="true" data-id="element-307" data-mp="2pg1slp25lum6" />
                  {shop.phone}
                </a>
              </li>
            </ul>
          </motion.nav>}
      </AnimatePresence>
    </header>;
}